import { gql } from '@apollo/client';
import axios from 'axios';
import { apolloClient } from '../apollo/client';
import { dispatchGlobalError } from '../context/ApiErrorContext';
import { dispatchLogout } from '../context/AuthContext';
import type {
  LigaContaIugu,
  LigaContaIuguDocumentos,
  LigaContaIuguInput,
  LigaContaIuguDocumentosInput,
  TipoPessoa,
  TipoConta,
} from '../types/ligaContaIugu';

// Fragments reutilizáveis
export const LIGA_CONTA_IUGU_FRAGMENT = gql`
  fragment LigaContaIuguFragment on LigaContaIugu {
    id
    accountId
    nome
    verificada
    ativa
    criadoEm
    atualizadoEm
  }
`;

export const LIGA_CONTA_IUGU_DOCUMENTOS_FRAGMENT = gql`
  fragment LigaContaIuguDocumentosFragment on LigaContaIuguDocumentos {
    id
    tipoPessoa
    cnpj
    cpf
    nomeEmpresa
    nomeResp
    respCpf
    nome
    telefone
    banco
    bancoAg
    tipoConta
    bancoCc
    arquivoIdUrl
    selfieUrl
    comprovanteEnderecoUrl
    relatorioFinanceiroUrl
    contratoSocialUrl
    priceRange
    physicalProducts
    businessType
    automaticTransfer
    estimatedRevenue
    criadoEm
    atualizadoEm
  }
`;

// Re-export types for convenience
export type {
  LigaContaIugu,
  LigaContaIuguDocumentos,
  LigaContaIuguInput,
  LigaContaIuguDocumentosInput,
  TipoPessoa,
  TipoConta,
} from '../types/ligaContaIugu';

// Queries
export const LIGA_CONTA_IUGU_POR_LIGA_ID = gql`
  ${LIGA_CONTA_IUGU_FRAGMENT}
  query LigaContaIuguPorLigaId($ligaId: ID!) {
    ligaContaIuguPorLigaId(ligaId: $ligaId) {
      ...LigaContaIuguFragment
    }
  }
`;

export const LIGA_CONTA_IUGU_DOCUMENTOS_POR_LIGA_ID = gql`
  ${LIGA_CONTA_IUGU_DOCUMENTOS_FRAGMENT}
  query LigaContaIuguDocumentosPorLigaId($ligaId: ID!) {
    ligaContaIuguDocumentosPorLigaId(ligaId: $ligaId) {
      ...LigaContaIuguDocumentosFragment
    }
  }
`;

export const LIGA_CONTA_IUGU_POR_ID = gql`
  ${LIGA_CONTA_IUGU_FRAGMENT}
  query LigaContaIuguPorId($id: ID!) {
    ligaContaIuguPorId(id: $id) {
      ...LigaContaIuguFragment
    }
  }
`;

export const LIGA_CONTA_IUGU_DOCUMENTOS_POR_ID = gql`
  ${LIGA_CONTA_IUGU_DOCUMENTOS_FRAGMENT}
  query LigaContaIuguDocumentosPorId($id: ID!) {
    ligaContaIuguDocumentosPorId(id: $id) {
      ...LigaContaIuguDocumentosFragment
    }
  }
`;

export const LIGAS_CONTA_IUGU = gql`
  ${LIGA_CONTA_IUGU_FRAGMENT}
  query LigasContaIugu {
    ligasContaIugu {
      ...LigaContaIuguFragment
    }
  }
`;

export const LIGAS_CONTA_IUGU_DOCUMENTOS = gql`
  ${LIGA_CONTA_IUGU_DOCUMENTOS_FRAGMENT}
  query LigasContaIuguDocumentos {
    ligasContaIuguDocumentos {
      ...LigaContaIuguDocumentosFragment
    }
  }
`;

// Mutations
export const CRIAR_LIGA_CONTA_IUGU = gql`
  ${LIGA_CONTA_IUGU_FRAGMENT}
  mutation CriarLigaContaIugu($input: LigaContaIuguInput!) {
    criarLigaContaIugu(input: $input) {
      ...LigaContaIuguFragment
    }
  }
`;

export const ATUALIZAR_LIGA_CONTA_IUGU = gql`
  ${LIGA_CONTA_IUGU_FRAGMENT}
  mutation AtualizarLigaContaIugu($id: ID!, $input: LigaContaIuguInput!) {
    atualizarLigaContaIugu(id: $id, input: $input) {
      ...LigaContaIuguFragment
    }
  }
`;

export const DELETAR_LIGA_CONTA_IUGU = gql`
  mutation DeletarLigaContaIugu($id: ID!) {
    deletarLigaContaIugu(id: $id)
  }
`;

export const CRIAR_LIGA_CONTA_IUGU_DOCUMENTOS = gql`
  ${LIGA_CONTA_IUGU_DOCUMENTOS_FRAGMENT}
  mutation CriarLigaContaIuguDocumentos($input: LigaContaIuguDocumentosInput!) {
    criarLigaContaIuguDocumentos(input: $input) {
      ...LigaContaIuguDocumentosFragment
    }
  }
`;

export const ATUALIZAR_LIGA_CONTA_IUGU_DOCUMENTOS = gql`
  ${LIGA_CONTA_IUGU_DOCUMENTOS_FRAGMENT}
  mutation AtualizarLigaContaIuguDocumentos($id: ID!, $input: LigaContaIuguDocumentosInput!) {
    atualizarLigaContaIuguDocumentos(id: $id, input: $input) {
      ...LigaContaIuguDocumentosFragment
    }
  }
`;

export const DELETAR_LIGA_CONTA_IUGU_DOCUMENTOS = gql`
  mutation DeletarLigaContaIuguDocumentos($id: ID!) {
    deletarLigaContaIuguDocumentos(id: $id)
  }
`;

// Interfaces para variáveis das queries/mutations
export interface LigaContaIuguPorLigaIdData {
  ligaContaIuguPorLigaId: LigaContaIugu | null;
}

export interface LigaContaIuguPorLigaIdVars {
  ligaId: string;
}

export interface LigaContaIuguDocumentosPorLigaIdData {
  ligaContaIuguDocumentosPorLigaId: LigaContaIuguDocumentos | null;
}

export interface LigaContaIuguDocumentosPorLigaIdVars {
  ligaId: string;
}

export interface LigaContaIuguPorIdData {
  ligaContaIuguPorId: LigaContaIugu;
}

export interface LigaContaIuguPorIdVars {
  id: string;
}

export interface LigaContaIuguDocumentosPorIdData {
  ligaContaIuguDocumentosPorId: LigaContaIuguDocumentos;
}

export interface LigaContaIuguDocumentosPorIdVars {
  id: string;
}

export interface LigasContaIuguData {
  ligasContaIugu: LigaContaIugu[];
}

export interface LigasContaIuguDocumentosData {
  ligasContaIuguDocumentos: LigaContaIuguDocumentos[];
}

export interface CriarLigaContaIuguData {
  criarLigaContaIugu: LigaContaIugu;
}

export interface CriarLigaContaIuguVars {
  input: LigaContaIuguInput;
}

export interface AtualizarLigaContaIuguData {
  atualizarLigaContaIugu: LigaContaIugu;
}

export interface AtualizarLigaContaIuguVars {
  id: string;
  input: LigaContaIuguInput;
}

export interface DeletarLigaContaIuguData {
  deletarLigaContaIugu: boolean;
}

export interface DeletarLigaContaIuguVars {
  id: string;
}

export interface CriarLigaContaIuguDocumentosData {
  criarLigaContaIuguDocumentos: LigaContaIuguDocumentos;
}

export interface CriarLigaContaIuguDocumentosVars {
  input: LigaContaIuguDocumentosInput;
}

export interface AtualizarLigaContaIuguDocumentosData {
  atualizarLigaContaIuguDocumentos: LigaContaIuguDocumentos;
}

export interface AtualizarLigaContaIuguDocumentosVars {
  id: string;
  input: LigaContaIuguDocumentosInput;
}

export interface DeletarLigaContaIuguDocumentosData {
  deletarLigaContaIuguDocumentos: boolean;
}

export interface DeletarLigaContaIuguDocumentosVars {
  id: string;
}

// Funções de serviço com tratamento de erro
const handleError = (error: any, defaultMessage: string): never => {
  if (error.graphQLErrors) {
    const graphQLError = error.graphQLErrors[0];
    if (graphQLError?.extensions?.code === 'UNAUTHENTICATED') {
      dispatchLogout();
      if (typeof window !== 'undefined') {
        window.location.href = '/login';
      }
      throw error;
    }
  }

  if (error.response?.status === 401) {
    dispatchLogout();
    if (typeof window !== 'undefined') {
      window.location.href = '/login';
    }
    throw error;
  }

  let message = defaultMessage;
  if (error.graphQLErrors?.[0]?.message) {
    message = error.graphQLErrors[0].message;
  } else if (error.message) {
    message = error.message;
  }

  dispatchGlobalError(message);
  throw error;
};

// Service functions - LigaContaIugu
export const buscarLigaContaIuguPorLigaId = async (ligaId: string): Promise<LigaContaIugu | null> => {
  try {
    const { data } = await apolloClient.query<LigaContaIuguPorLigaIdData, LigaContaIuguPorLigaIdVars>({
      query: LIGA_CONTA_IUGU_POR_LIGA_ID,
      variables: { ligaId },
      fetchPolicy: 'no-cache',
    });
    return data.ligaContaIuguPorLigaId;
  } catch (error: any) {
    return handleError(error, 'Erro ao buscar conta IUGU da liga. Tente novamente.');
  }
};

export const buscarLigaContaIuguPorId = async (id: string): Promise<LigaContaIugu> => {
  try {
    const { data } = await apolloClient.query<LigaContaIuguPorIdData, LigaContaIuguPorIdVars>({
      query: LIGA_CONTA_IUGU_POR_ID,
      variables: { id },
      fetchPolicy: 'no-cache',
    });
    return data.ligaContaIuguPorId;
  } catch (error: any) {
    return handleError(error, 'Erro ao buscar conta IUGU. Tente novamente.');
  }
};

export const buscarLigasContaIugu = async (): Promise<LigaContaIugu[]> => {
  try {
    const { data } = await apolloClient.query<LigasContaIuguData>({
      query: LIGAS_CONTA_IUGU,
      fetchPolicy: 'no-cache',
    });
    return data.ligasContaIugu;
  } catch (error: any) {
    return handleError(error, 'Erro ao buscar contas IUGU. Tente novamente.');
  }
};

export const criarLigaContaIugu = async (input: LigaContaIuguInput): Promise<LigaContaIugu> => {
  try {
    const { data } = await apolloClient.mutate<CriarLigaContaIuguData, CriarLigaContaIuguVars>({
      mutation: CRIAR_LIGA_CONTA_IUGU,
      variables: { input },
    });
    if (!data?.criarLigaContaIugu) {
      throw new Error('Não foi possível criar a conta IUGU.');
    }
    return data.criarLigaContaIugu;
  } catch (error: any) {
    return handleError(error, 'Erro ao criar conta IUGU. Tente novamente.');
  }
};

export const atualizarLigaContaIugu = async (id: string, input: LigaContaIuguInput): Promise<LigaContaIugu> => {
  try {
    const { data } = await apolloClient.mutate<AtualizarLigaContaIuguData, AtualizarLigaContaIuguVars>({
      mutation: ATUALIZAR_LIGA_CONTA_IUGU,
      variables: { id, input },
    });
    if (!data?.atualizarLigaContaIugu) {
      throw new Error('Não foi possível atualizar a conta IUGU.');
    }
    return data.atualizarLigaContaIugu;
  } catch (error: any) {
    return handleError(error, 'Erro ao atualizar conta IUGU. Tente novamente.');
  }
};

export const deletarLigaContaIugu = async (id: string): Promise<boolean> => {
  try {
    const { data } = await apolloClient.mutate<DeletarLigaContaIuguData, DeletarLigaContaIuguVars>({
      mutation: DELETAR_LIGA_CONTA_IUGU,
      variables: { id },
    });
    return data?.deletarLigaContaIugu ?? false;
  } catch (error: any) {
    return handleError(error, 'Erro ao deletar conta IUGU. Tente novamente.');
  }
};

// Service functions - LigaContaIuguDocumentos
export const buscarLigaContaIuguDocumentosPorLigaId = async (
  ligaId: string
): Promise<LigaContaIuguDocumentos | null> => {
  try {
    const { data } = await apolloClient.query<
      LigaContaIuguDocumentosPorLigaIdData,
      LigaContaIuguDocumentosPorLigaIdVars
    >({
      query: LIGA_CONTA_IUGU_DOCUMENTOS_POR_LIGA_ID,
      variables: { ligaId },
      fetchPolicy: 'no-cache',
    });
    return data.ligaContaIuguDocumentosPorLigaId;
  } catch (error: any) {
    return handleError(error, 'Erro ao buscar documentação da conta IUGU. Tente novamente.');
  }
};

export const buscarLigaContaIuguDocumentosPorId = async (id: string): Promise<LigaContaIuguDocumentos> => {
  try {
    const { data } = await apolloClient.query<LigaContaIuguDocumentosPorIdData, LigaContaIuguDocumentosPorIdVars>({
      query: LIGA_CONTA_IUGU_DOCUMENTOS_POR_ID,
      variables: { id },
      fetchPolicy: 'no-cache',
    });
    return data.ligaContaIuguDocumentosPorId;
  } catch (error: any) {
    return handleError(error, 'Erro ao buscar documentação. Tente novamente.');
  }
};

export const buscarLigasContaIuguDocumentos = async (): Promise<LigaContaIuguDocumentos[]> => {
  try {
    const { data } = await apolloClient.query<LigasContaIuguDocumentosData>({
      query: LIGAS_CONTA_IUGU_DOCUMENTOS,
      fetchPolicy: 'no-cache',
    });
    return data.ligasContaIuguDocumentos;
  } catch (error: any) {
    return handleError(error, 'Erro ao buscar documentações. Tente novamente.');
  }
};

export const criarLigaContaIuguDocumentos = async (
  input: LigaContaIuguDocumentosInput
): Promise<LigaContaIuguDocumentos> => {
  try {
    const { data } = await apolloClient.mutate<
      CriarLigaContaIuguDocumentosData,
      CriarLigaContaIuguDocumentosVars
    >({
      mutation: CRIAR_LIGA_CONTA_IUGU_DOCUMENTOS,
      variables: { input },
    });
    if (!data?.criarLigaContaIuguDocumentos) {
      throw new Error('Não foi possível criar a documentação.');
    }
    return data.criarLigaContaIuguDocumentos;
  } catch (error: any) {
    return handleError(error, 'Erro ao criar documentação. Tente novamente.');
  }
};

export const atualizarLigaContaIuguDocumentos = async (
  id: string,
  input: LigaContaIuguDocumentosInput
): Promise<LigaContaIuguDocumentos> => {
  try {
    const { data } = await apolloClient.mutate<
      AtualizarLigaContaIuguDocumentosData,
      AtualizarLigaContaIuguDocumentosVars
    >({
      mutation: ATUALIZAR_LIGA_CONTA_IUGU_DOCUMENTOS,
      variables: { id, input },
    });
    if (!data?.atualizarLigaContaIuguDocumentos) {
      throw new Error('Não foi possível atualizar a documentação.');
    }
    return data.atualizarLigaContaIuguDocumentos;
  } catch (error: any) {
    return handleError(error, 'Erro ao atualizar documentação. Tente novamente.');
  }
};

export const deletarLigaContaIuguDocumentos = async (id: string): Promise<boolean> => {
  try {
    const { data } = await apolloClient.mutate<
      DeletarLigaContaIuguDocumentosData,
      DeletarLigaContaIuguDocumentosVars
    >({
      mutation: DELETAR_LIGA_CONTA_IUGU_DOCUMENTOS,
      variables: { id },
    });
    return data?.deletarLigaContaIuguDocumentos ?? false;
  } catch (error: any) {
    return handleError(error, 'Erro ao deletar documentação. Tente novamente.');
  }
};

// REST API functions for file uploads

/**
 * Tipo para os dados de documentação (sem arquivos)
 */
export interface LigaContaIuguDocumentosData {
  ligaId: string;
  tipoPessoa: TipoPessoa;
  cpf?: string | null;
  nome?: string | null;
  cnpj?: string | null;
  nomeEmpresa?: string | null;
  nomeResp?: string | null;
  respCpf?: string | null;
  telefone?: string | null;
  banco?: string | null;
  bancoAg?: string | null;
  tipoConta?: TipoConta | null;
  bancoCc?: string | null;
  enderecoId?: string | null;
  arquivoIdUrl?: string | null;
  selfieUrl?: string | null;
  comprovanteEnderecoUrl?: string | null;
  relatorioFinanceiroUrl?: string | null;
  contratoSocialUrl?: string | null;
  priceRange?: string | null;
  physicalProducts?: boolean | null;
  businessType?: string | null;
  automaticTransfer?: boolean | null;
  estimatedRevenue?: string | null;
}

/**
 * Tipo para os arquivos de upload
 * - File: envia o arquivo
 * - '': sinaliza remoção do arquivo existente
 * - undefined: campo não alterado (não enviado)
 */
export interface LigaContaIuguDocumentosArquivos {
  arquivoId?: File | '';
  selfie?: File | '';
  comprovanteEndereco?: File | '';
  relatorioFinanceiro?: File | '';
  contratoSocial?: File | '';
}

const getRestApiBaseUrl = (): string => {
  const graphqlEndpoint = process.env.REACT_APP_GRAPHQL_ENDPOINT || 'http://localhost:4000/graphql';
  return graphqlEndpoint.replace('/graphql', '');
};

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * Cria documentação da conta IUGU com upload de múltiplos arquivos
 */
export const criarDocumentosComArquivos = async (
  data: LigaContaIuguDocumentosData,
  arquivos: LigaContaIuguDocumentosArquivos
): Promise<LigaContaIuguDocumentos> => {
  try {
    const formData = new FormData();

    // Adicionar data como JSON string
    formData.append('data', JSON.stringify(data));

    // Adicionar arquivos se fornecidos (File = enviar, '' = remover, undefined = não alterado)
    if (arquivos.arquivoId !== undefined) {
      formData.append('arquivoId', arquivos.arquivoId);
    }
    if (arquivos.selfie !== undefined) {
      formData.append('selfie', arquivos.selfie);
    }
    if (arquivos.comprovanteEndereco !== undefined) {
      formData.append('comprovanteEndereco', arquivos.comprovanteEndereco);
    }
    if (arquivos.relatorioFinanceiro !== undefined) {
      formData.append('relatorioFinanceiro', arquivos.relatorioFinanceiro);
    }
    if (arquivos.contratoSocial !== undefined) {
      formData.append('contratoSocial', arquivos.contratoSocial);
    }

    const response = await axios.post(
      `${getRestApiBaseUrl()}api/liga-conta-iugu-documentos`,
      formData,
      {
        headers: {
          ...getAuthHeaders(),
          // NÃO incluir Content-Type - deixar o browser definir automaticamente
        },
      }
    );

    return response.data;
  } catch (error: any) {
    if (error.response?.status === 401 || error.response?.status === 403) {
      dispatchLogout();
      if (typeof window !== 'undefined') {
        window.location.href = '/login';
      }
      throw error;
    }
    if (error.response?.status === 400) {
      const message = error.response?.data?.error || 'Dados inválidos. Verifique os campos e tente novamente.';
      dispatchGlobalError(message);
      throw new Error(message);
    }
    const message = error.response?.data?.error || 'Erro ao criar documentação. Tente novamente.';
    dispatchGlobalError(message);
    throw new Error(message);
  }
};

/**
 * Atualiza documentação da conta IUGU com upload de múltiplos arquivos
 */
export const atualizarDocumentosComArquivos = async (
  id: string,
  data: Partial<LigaContaIuguDocumentosData>,
  arquivos: LigaContaIuguDocumentosArquivos
): Promise<LigaContaIuguDocumentos> => {
  try {
    const formData = new FormData();

    // Adicionar data como JSON string
    formData.append('data', JSON.stringify(data));

    // Adicionar arquivos se fornecidos (File = enviar, '' = remover, undefined = não alterado)
    if (arquivos.arquivoId !== undefined) {
      formData.append('arquivoId', arquivos.arquivoId);
    }
    if (arquivos.selfie !== undefined) {
      formData.append('selfie', arquivos.selfie);
    }
    if (arquivos.comprovanteEndereco !== undefined) {
      formData.append('comprovanteEndereco', arquivos.comprovanteEndereco);
    }
    if (arquivos.relatorioFinanceiro !== undefined) {
      formData.append('relatorioFinanceiro', arquivos.relatorioFinanceiro);
    }
    if (arquivos.contratoSocial !== undefined) {
      formData.append('contratoSocial', arquivos.contratoSocial);
    }

    const response = await axios.put(
      `${getRestApiBaseUrl()}api/liga-conta-iugu-documentos/${id}`,
      formData,
      {
        headers: {
          ...getAuthHeaders(),
          // NÃO incluir Content-Type - deixar o browser definir automaticamente
        },
      }
    );

    return response.data;
  } catch (error: any) {
    if (error.response?.status === 401 || error.response?.status === 403) {
      dispatchLogout();
      if (typeof window !== 'undefined') {
        window.location.href = '/login';
      }
      throw error;
    }
    if (error.response?.status === 404) {
      const message = 'Documentação não encontrada.';
      dispatchGlobalError(message);
      throw new Error(message);
    }
    if (error.response?.status === 400) {
      const message = error.response?.data?.error || 'Dados inválidos. Verifique os campos e tente novamente.';
      dispatchGlobalError(message);
      throw new Error(message);
    }
    const message = error.response?.data?.error || 'Erro ao atualizar documentação. Tente novamente.';
    dispatchGlobalError(message);
    throw new Error(message);
  }
};
